'use client'
import clsx from 'clsx'
import { type ChangeEvent, type InputHTMLAttributes, useState } from 'react'

import { Input as UIInput } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface UIInputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  label?: string
  labelPosition?: 'floating' | 'top'
  inputClassName?: string
}

export const Input = ({ placeholder, error, onChange, label, labelPosition = 'floating', inputClassName, ...props }: UIInputProps) => {
  const [hasValue, setHasValue] = useState(!!props?.value || !!props?.defaultValue)
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div className={clsx('relative flex w-full')}>
      <label className="flex w-full flex-col">
        {label && labelPosition === 'top' && <span className="text-sm font-medium text-grey-900">{label}</span>}
        <UIInput
          placeholder={placeholder && (labelPosition === 'top' || !label) ? placeholder : undefined}
          className={cn(
            label && labelPosition === 'floating' && (hasValue || isFocused) ? 'pb-1 pt-5' : '',
            error ? 'enabled:border-error-500' : 'focus-visible:border-primary-500',
            inputClassName,
          )}
          onChange={(e) => {
            if (onChange) onChange(e)
            setHasValue(!!e.target.value)
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false)
          }}
          {...props}
        />
        {label && labelPosition === 'floating' && (
          <p
            className={clsx(
              'absolute start-3 top-3 z-1 origin-0 text-base duration-300',
              hasValue || isFocused ? '-translate-y-2 text-xs' : '',
              error ? 'text-error-700' : `${isFocused ? 'text-primary-500' : 'text-grey-500'}`,
              props.disabled && 'text-grey-500',
            )}
          >
            {label}
          </p>
        )}
        {error && !props.disabled && <span className="text-sm font-medium text-error-700">{error}</span>}
      </label>
    </div>
  )
}
